<template>
  <custom-callout border-color-hex="#FB7058" class="mb-4" :has-toggle="false">
    <icon-view
      class="mt-2"
      icon="fa fa-file-user mb-1"
      icon-background="#FB7058"
      icon-color="#fff"
      icon-size="14px"
      title="Añadir Configuración de Costo"
    />
    <validation-observer ref="costManagementRef" novalidate tag="form">
      <div class="row mt-3 mr-3">
        <div class="col-md-4 col-sm-12 mt-3 mt-md-0">
          <div class="color-gray-6 font-14 mb-1">Servicio:</div>
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-drop-down
              v-model="model.service"
              :clearable="false"
              custom-error-msg="El servicio es requerido."
              :error="errors[0]"
              fieldkey="id"
              fieldname="name"
              :options="services"
              placeholder="Seleccione"
              @input="onChangeServiceHandler"
            />
          </validation-provider>
        </div>
        <div class="col-md-4 col-sm-12 mt-3 mt-md-0">
          <div class="color-gray-6 font-14 mb-1">Disciplina:</div>
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-drop-down
              v-model="model.discipline"
              :clearable="true"
              custom-error-msg="La disciplina es requerida."
              :error="errors[0]"
              fieldkey="id"
              fieldname="name"
              :options="disciplines"
              placeholder="Seleccione"
              @input="onChangeDisciplineHandler"
            />
          </validation-provider>
        </div>
        <div
          v-if="subDisciplines.length > 0"
          class="col-md-4 col-sm-12 mt-3 mt-md-0"
        >
          <div class="color-gray-6 font-14 mb-1">SubDisciplina:</div>
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-drop-down
              v-model="model.subDisciplineTypeId"
              :clearable="true"
              custom-error-msg="La SubDisciplina es requerida."
              :error="errors[0]"
              fieldkey="id"
              fieldname="name"
              :options="subDisciplines"
              placeholder="Seleccione"
            />
          </validation-provider>
        </div>
        <div
          v-if="isTherapyService"
          class="col-md-4 col-sm-12 mt-3 mt-md-0"
          :class="{ 'mt-md-3': subDisciplines.length > 0 }"
        >
          <div class="color-gray-6 font-14 mb-1">Modalidad:</div>
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-drop-down
              v-model="model.modalityType"
              :clearable="true"
              custom-error-msg="La modalidad es requerida."
              :error="errors[0]"
              fieldkey="id"
              fieldname="name"
              :options="modalityTypes"
              placeholder="Seleccione"
            />
          </validation-provider>
        </div>
        <div v-if="isTherapyService" class="col-md-4 col-sm-12 mt-3">
          <div class="color-gray-6 font-14 mb-1">Duración:</div>
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-drop-down
              v-model="model.duration"
              :clearable="true"
              custom-error-msg="La duración es requerida."
              :error="errors[0]"
              fieldkey="id"
              fieldname="name"
              :options="durations"
              placeholder="Seleccione"
            />
          </validation-provider>
        </div>
        <div v-if="isTherapyService" class="col-md-4 col-sm-12 mt-3">
          <div class="color-gray-6 font-14 mb-1">Lugar:</div>
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-drop-down
              v-model="model.locationType"
              :clearable="true"
              custom-error-msg="El lugar es requerido."
              :error="errors[0]"
              fieldkey="id"
              fieldname="name"
              :options="locationTypes"
              placeholder="Seleccione"
            />
          </validation-provider>
        </div>
        <div
          class="col-md-4 col-sm-12"
          :class="{
            'mt-3': isTherapyService,
          }"
        >
          <div class="color-gray-6 font-14 mb-1">Costo Máximo:</div>
          <validation-provider
            v-slot="{ errors }"
            rules="required|differentFromZero"
          >
            <custom-input
              v-model="model.costMax"
              custom-error-msg="El costo máximo es requerido."
              :error="errors[0]"
              type="number"
            />
          </validation-provider>
        </div>
        <div class="col-12 mt-3 d-flex justify-content-end align-items-center">
          <action-button
            i="fas fa-times-circle"
            text="Cancelar"
            variant="outline-danger m-2 px-4"
            @click="onCancelHandler"
          />
          <action-button
            i="fas fa-save"
            text="Guardar"
            variant="success px-4"
            @click="onSaveHandler"
          />
        </div>
      </div>
    </validation-observer>
  </custom-callout>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import CustomCallout from 'custom-callout';
import ActionButton from 'ActionButton';
import IconView from 'icon-view';
import CustomInput from 'CustomInput';
import CustomDropDown from 'custom-drop-down';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import codeServiceTypes from '@/utils/constants/codeServiceTypes.js';
import disciplineTypes from '@/utils/constants/disciplineTypes.js';
import {
  getDisciplinesRP as _getDisciplinesRP,
  getSubDisciplinesRP as _getSubDisciplinesRP,
} from '@/services/uspqrp/service-cost.api.js';

export default {
  name: 'CostManagementServices',
  mixins: [alertMixin],
  components: {
    ValidationObserver,
    ValidationProvider,
    CustomCallout,
    ActionButton,
    IconView,
    CustomInput,
    CustomDropDown,
  },
  props: {
    services: {
      type: Array,
      default: () => [],
    },
    modalityTypes: {
      type: Array,
      default: () => [],
    },
    durations: {
      type: Array,
      default: () => [],
    },
    locationTypes: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    model: {
      service: null,
      discipline: null,
      modalityType: null,
      duration: null,
      locationType: null,
      costMax: null,
      subDisciplineTypeId: null,
    },
    disciplines: [],
    subDisciplines: [],
  }),
  computed: {
    isTherapyService() {
      return this.model.service?.id === codeServiceTypes.Therapy;
    },
  },
  methods: {
    onCancelHandler() {
      this.$emit('on-cancel');
    },
    async onChangeServiceHandler(value) {
      if (this.isTherapyService) {
        this.model = {
          ...this.model,
          modalityType: null,
          duration: null,
          locationType: null,
        };
      }
      this.model.discipline = null;
      if (value?.id) {
        this.disciplines = (await _getDisciplinesRP(value.id)).data;
      } else {
        this.disciplines = [];
      }
    },
    async onSaveHandler() {
      const isValid = await this.$refs.costManagementRef.validate();
      if (!isValid) {
        this.ShowToast(
          'Alerta',
          'Favor completar la información solicitada.',
          'error'
        );
        return;
      }
      this.$emit('on-save', { ...this.model });
    },
    async onChangeDisciplineHandler(value) {
      if (value?.id) {
        if (value.id == disciplineTypes.TerapiaEntrenamientoAuditivo) {
          this.subDisciplines = (await _getSubDisciplinesRP(value.id)).data;
          return;
        }
        this.model.SubDisciplineTypeId = null;
        this.subDisciplines = [];
      } else {
        this.model.SubDisciplineTypeId = null;
        this.subDisciplines = [];
      }
    },
  },
};
</script>
